import { formatISO, isSameDay } from 'date-fns'
import React, { useRef, useState } from 'react'
import { formatDate } from '@/lib/date'
import { DateRangeInput } from './internal/DateRangeInput'
import { FilterSelect } from './internal/FilterSelect'
import { FilterSelectOptions } from './internal/FilterSelectOptions'
import { useOnClickOutside } from '../../../hooks/useOnClickOutside'
export type DateRange = [Date | null, Date | null]
export type DateRangeOptions = { [optionLabel: string]: DateRange }

type Props = {
  label: string
  options: DateRangeOptions
  selected: DateRange
  isOptionsOpen: boolean
  onSelect: (range: DateRange, byInput: boolean) => void
  onClick: () => void
  onClickOutside: () => void
  isLabelOut?: boolean
  hasError?: boolean
  className?: string
  isEnableInput?: boolean
}

export type DateRangeError = {
  message: string
  position: Array<'from' | 'to'>
}

export const DateRangeFilterSelect: React.FC<Props> = ({
  isEnableInput = true,
  ...props
}) => {
  const [error, setError] = useState<DateRangeError | null>(null)
  const ref = useRef<HTMLDivElement>(null)

  useOnClickOutside([ref], () => {
    if (!props.isOptionsOpen || !props.onClickOutside) {
      return
    }
    return props.onClickOutside()
  })

  const handleSelect = (range: DateRange, options: { byInput: boolean }) => {
    props.onSelect(range, options.byInput)
  }

  return (
    <FilterSelect
      label={props.label}
      selectedText={selectedText(props as Props)}
      isLabelOut={props.isLabelOut}
      onClick={props.onClick}
      errorText={error ? '日付が無効です' : undefined}
      className={props.className}
      ref={ref}
      options={
        <FilterSelectOptions<DateRange>
          onSelect={(range) => handleSelect(range, { byInput: false })}
          selected={props.selected}
          options={Object.values(props.options)}
          labels={Object.keys(props.options)}
          isSelectedFn={isSameDateRange}
          isOpen={props.isOptionsOpen}
        >
          {isEnableInput && (
            <DateRangeInput
              range={props.selected}
              onChange={(range) => handleSelect(range, { byInput: true })}
              error={error}
              onError={setError}
            />
          )}
        </FilterSelectOptions>
      }
    />
  )
}

export const isSameDateRange = (range1: DateRange, range2: DateRange) => {
  if (range1.every((d) => d === null) && range2.every((d) => d === null)) {
    return true
  }
  return isSameDay(range1[0]!, range2[0]!) && isSameDay(range1[1]!, range2[1]!)
}

const format = (date: Date) => formatDate(formatISO(date), 'yyyy/MM/dd')

const selectedText = (props: Props): string | null => {
  const [from, to] = props.selected

  if (from === null && to === null) {
    return null
  }
  const i = Object.values(props.options).findIndex((r) =>
    isSameDateRange([from, to], r)
  )
  if (i >= 0) {
    return Object.keys(props.options)[i]
  }

  if (from && to) {
    return `${format(from)} 〜 ${format(to)}`
  } else if (from) {
    return `${format(from)} 〜`
  } else if (to) {
    return `〜 ${format(to)}`
  } else {
    return null
  }
}
